import { frontDoorPath } from "@softmax/web-features/coworlds";
import type { SearchResultRow } from "@softmax/product-ux/search";
import { playerLabel } from "@softmax/web-ux/player-label";
import { buildHref, type GameDestinationContext } from "@softmax/web-addresses";
import type { GameRouteScope } from "@softmax/web-addresses";
import type { Schemas } from "@softmax/observatory-client/types";
import {
  buildDirectoryCoworlds,
  type DirectoryEntryLike,
} from "@softmax/web-features/site/records";
type Corpus = {
  id: string;
  name: string;
  game: GameRouteScope | null;
};
export function matchCoworlds({
  directory,
  query,
  limit,
  inProgress,
}: {
  directory: DirectoryEntryLike[];
  query: string;
  limit: number;
  inProgress?: ReadonlyMap<string, string> | undefined;
}): SearchResultRow[] {
  const needle = query.toLowerCase();
  return buildDirectoryCoworlds(directory)
    .filter(
      (entry) =>
        entry.name.toLowerCase().includes(needle) ||
        entry.corpusSlug.toLowerCase().includes(needle) ||
        entry.route.game.slug.toLowerCase().includes(needle),
    )
    .slice(0, limit)
    .map((entry) => ({
      key: entry.route.game.id,
      // A coworld the viewer is already in opens on /play, not its store page.
      href:
        inProgress?.get(entry.route.game.slug) ||
        frontDoorPath({ corpusSlug: entry.corpusSlug, route: entry.route }),
      label: entry.name,
    }));
}
export function policySearchRows(
  rows: Schemas["V2PolicyVersionRow"][],
): SearchResultRow[] {
  return rows.map((row) => ({
    key: row.policy_version_id,
    href: buildHref({
      name: "policyVersion",
      policyVersionId: row.policy_version_id,
    }),
    label: `${row.policy_name}:v${row.version}`,
    secondary: row.player_name
      ? playerLabel({
          name: row.player_name,
          ownerName: row.owner_name ?? null,
        })
      : undefined,
  }));
}
// Global forum search omits corpus slugs, so only sources with known addresses can supply links.
export function forumSearchRows({
  source,
  page,
}: {
  source: Corpus;
  page: Schemas["ForumSearchPage"];
}): SearchResultRow[] {
  return page.entries.map(({ post }) => ({
    key: `${source.id}:${post.id}`,
    href: source.game
      ? buildHref({
          name: "game.forumPost",
          game: source.game,
          postId: post.id,
        })
      : buildHref({ name: "forumPost", postId: post.id }),
    label: post.title,
    secondary: `${source.name === "softmax" ? "Softmax" : source.name} · ${post.author?.name ?? "—"}`,
  }));
}
export function wikiSearchRows({
  source,
  result,
}: {
  source: Corpus;
  result: Schemas["WikiSearchPage"];
}): SearchResultRow[] {
  return result.entries.map(({ page }) => ({
    key: `${source.id}:${page.id}`,
    href: source.game
      ? buildHref({
          name: "game.wikiPage",
          game: source.game,
          page: page.slug.split("/"),
        })
      : buildHref({ name: "wikiPage", page: page.slug.split("/") }),
    label: page.title,
    secondary: `${source.name === "softmax" ? "Softmax" : source.name} wiki`,
  }));
}
/** The forums and wikis a search covers: Softmax's, and the coworld's own. */
export function corporaOf({
  coworld,
}: {
  coworld?:
    | {
        name: string;
        route: GameDestinationContext;
        hasWiki: boolean;
      }
    | undefined;
}): {
  forums: [Corpus, ...Corpus[]];
  wikis: [Corpus, ...Corpus[]];
} {
  const forums: [Corpus, ...Corpus[]] = [
    { id: "forum:softmax", name: "softmax", game: null },
  ];
  const wikis: [Corpus, ...Corpus[]] = [
    { id: "wiki:softmax", name: "softmax", game: null },
  ];

  if (coworld) {
    const game = coworld.route.game;
    forums.push({
      id: "forum:coworld",
      name: coworld.name,
      game,
    });

    if (coworld.hasWiki)
      wikis.push({
        id: "wiki:coworld",
        name: coworld.name,
        game,
      });
  }

  return { forums, wikis };
}
